import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, RotateCcw, Save } from 'lucide-react';
import GSTInput from '../GSTInput';
import ContactSuggestion from '../ContactSuggestion';
import AppSelect from '../shared/AppSelect';
import { PARTIES_THEME, formFieldCls, formLabelCls } from './partiesTheme';
import { doc, getDoc, serverTimestamp, updateDoc } from '../../firebase.config';
import { enforcedSetDoc as setDoc } from '../../utils/enforcedTracking';
import { getCollectionForCompany } from '../../utils/dataAdapter';
import {
  getServiceTagDocId,
  buildPreferredVendorPatch,
  promotePreferredVendorId,
  normalizePreferredVendorIds,
} from '../../utils/serviceTagRegistry';

export default function PartyFormPanel({
  embedded = true,
  inModal = false,
  formId = 'party-form',
  hideFormActions = false,
  editingPartyId,
  t,
  contactSuggestion,
  onAcceptContactSuggestion,
  onRejectContactSuggestion,
  handleSubmit,
  firmName,
  setFirmName,
  partyType,
  setPartyType,
  setIsServiceVendor,
  isServiceVendor,
  vendorServiceTags,
  setVendorServiceTags,
  serviceTagOptions,
  serviceTagMaster = [],
  personName,
  setPersonName,
  contact,
  setContact,
  isSearchingContact,
  isMobileDevice,
  email,
  setEmail,
  whatsapp,
  setWhatsapp,
  gstin,
  setGstin,
  setGstValidation,
  handleCompanyDetailsFetch,
  gstType,
  setGstType,
  pan,
  setPan,
  city,
  setCity,
  stateName,
  setStateName,
  indianStates,
  pincode,
  setPincode,
  creditPeriod,
  setCreditPeriod,
  creditLimit,
  setCreditLimit,
  openingBalance,
  setOpeningBalance,
  address,
  setAddress,
  clearPartyForm,
  db,
  companyId,
  appId,
  userId,
}) {
  const theme = PARTIES_THEME;
  const selectTrigger = `${formFieldCls} ${theme.fieldFocus} text-left min-h-[44px] flex items-center justify-between touch-manipulation`;
  const ob = parseFloat(openingBalance) || 0;
  const fieldGrid = inModal
    ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-3'
    : 'grid grid-cols-1 sm:grid-cols-2 gap-3';
  const wideGrid = inModal
    ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-3'
    : 'grid grid-cols-1 sm:grid-cols-2 gap-3';

  const formBody = (
    <>
      {contactSuggestion && (
        <ContactSuggestion
          contact={contactSuggestion.contact}
          confidence={contactSuggestion.confidence}
          onAccept={onAcceptContactSuggestion}
          onReject={onRejectContactSuggestion}
        />
      )}

      <form id={formId} onSubmit={handleSubmit} className={inModal ? 'space-y-5' : 'space-y-4'}>
        <section className={theme.sectionWrap} aria-labelledby="party-basic-heading">
          <h4 id="party-basic-heading" className={`${theme.sectionHead} mb-3`}>
            Basic details
          </h4>
          <div className={fieldGrid}>
            <div className={inModal ? 'lg:col-span-2' : 'sm:col-span-2'}>
              <label htmlFor="firmName" className={formLabelCls}>
                Party name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="firmName"
                value={firmName}
                onChange={(e) => setFirmName(e.target.value)}
                className={`${formFieldCls} ${theme.fieldFocus}`}
                placeholder="e.g. ABC Enterprises"
                required
              />
            </div>
            <div>
              <label htmlFor="partyType" className={formLabelCls}>
                Party type
              </label>
              <AppSelect
                id="partyType"
                value={partyType}
                onChange={(e) => {
                  const next = e.target.value;
                  setPartyType(next);
                  if (next === 'Seller' || next === 'Both') setIsServiceVendor(true);
                }}
                aria-label="Party type"
                sheetTitle="Party type"
                options={[
                  { value: 'Buyer', label: t('buyer') },
                  { value: 'Seller', label: t('seller') },
                  { value: 'Both', label: t('both') },
                ]}
                triggerClassName={selectTrigger}
              />
            </div>
            <div>
              <label htmlFor="personName" className={formLabelCls}>
                Contact person
              </label>
              <input
                type="text"
                id="personName"
                value={personName}
                onChange={(e) => setPersonName(e.target.value)}
                className={`${formFieldCls} ${theme.fieldFocus}`}
                placeholder="Optional"
              />
            </div>
          </div>
        </section>

        {(partyType === 'Seller' || partyType === 'Both') && (
          <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-3 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label className="inline-flex items-center gap-2 text-xs font-medium text-slate-800 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isServiceVendor}
                  onChange={(e) => setIsServiceVendor(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-indigo-600"
                />
                Service vendor
              </label>
              <Link to="/service-tags" className="text-xs font-medium text-indigo-600 hover:underline">
                Manage tags →
              </Link>
            </div>
            {isServiceVendor && serviceTagOptions.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {serviceTagOptions.map((tag) => {
                  const selected = vendorServiceTags.includes(tag);
                  return (
                    <button
                      key={tag}
                      type="button"
                      onClick={async () => {
                        const adding = !selected;
                        setVendorServiceTags((prev) =>
                          adding ? [...prev, tag] : prev.filter((t) => t !== tag)
                        );
                        if (!db || !companyId) return;
                        const col = getCollectionForCompany(db, { companyId, appId, userId }, 'serviceTags');
                        if (!col) return;
                        const tagRef = doc(col, getServiceTagDocId(serviceTagMaster, tag));
                        const snap = await getDoc(tagRef);
                        const existing = snap.exists() ? snap.data() : {};
                        if (adding && editingPartyId) {
                          const ids = promotePreferredVendorId(
                            normalizePreferredVendorIds(existing),
                            editingPartyId
                          );
                          await setDoc(
                            tagRef,
                            {
                              normalizedName: tag,
                              name: tag,
                              ...buildPreferredVendorPatch(ids, [
                                { id: editingPartyId, firmName: firmName.trim() },
                              ]),
                              updatedAt: serverTimestamp(),
                            },
                            { merge: true }
                          );
                        } else if (!adding && editingPartyId) {
                          const ids = normalizePreferredVendorIds(existing).filter(
                            (id) => id !== editingPartyId
                          );
                          await updateDoc(tagRef, {
                            ...buildPreferredVendorPatch(ids, []),
                            updatedAt: serverTimestamp(),
                          });
                        }
                      }}
                      className={`capitalize px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                        selected
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white text-slate-700 border-slate-300 hover:border-indigo-300'
                      }`}
                    >
                      {tag}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <section className={theme.sectionWrap} aria-labelledby="party-contact-heading">
          <h4 id="party-contact-heading" className={`${theme.sectionHead} mb-3`}>
            Contact
          </h4>
          <div className={wideGrid}>
            <div>
              <label htmlFor="contact" className={formLabelCls}>
                Phone
                {isSearchingContact && (
                  <span className="ml-1 text-sky-600 font-normal">Searching…</span>
                )}
              </label>
              <input
                type="tel"
                id="contact"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                className={`${formFieldCls} ${theme.fieldFocus}`}
                placeholder="10-digit mobile"
              />
              {isMobileDevice && (
                <p className="mt-1 text-[10px] text-slate-500">Auto-fill from synced contacts when available</p>
              )}
            </div>
            <div>
              <label htmlFor="email" className={formLabelCls}>
                Email
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`${formFieldCls} ${theme.fieldFocus}`}
              />
            </div>
            <div className={inModal ? 'lg:col-span-1' : 'sm:col-span-2'}>
              <label htmlFor="whatsapp" className={formLabelCls}>
                WhatsApp
              </label>
              <input
                type="tel"
                id="whatsapp"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                className={`${formFieldCls} ${theme.fieldFocus}`}
              />
            </div>
          </div>
        </section>

        <details className={`${theme.sectionWrap} group`} open={inModal || undefined}>
          <summary className="cursor-pointer list-none flex items-center gap-2 text-sm font-medium text-slate-800">
            <BookOpen className="h-4 w-4 text-emerald-600" aria-hidden />
            GST & tax
            {!inModal && (
              <span className="text-xs font-normal text-slate-500 ml-1">(tap to expand)</span>
            )}
          </summary>
          <div className={`mt-3 ${wideGrid}`}>
            <div className={inModal ? 'lg:col-span-3' : 'sm:col-span-2'}>
              <GSTInput
                value={gstin}
                onChange={setGstin}
                onValidationChange={setGstValidation}
                onCompanyDetailsFetch={handleCompanyDetailsFetch}
                required={gstType === 'Regular'}
                placeholder={
                  gstType === 'Regular'
                    ? '15-digit GSTIN (required for Regular)'
                    : 'GSTIN (optional)'
                }
                showExamples
                showStateInfo
                showPANInfo
                autoFetchDetails
                apiProvider="mock"
              />
            </div>
            <div>
              <label htmlFor="gstType" className={formLabelCls}>
                GST type
              </label>
              <AppSelect
                id="gstType"
                value={gstType}
                onChange={(e) => setGstType(e.target.value)}
                aria-label="GST type"
                sheetTitle="GST type"
                options={[
                  { value: 'Regular', label: t('regular') },
                  { value: 'Composition', label: t('composition') },
                  { value: 'Unregistered', label: t('unregistered') },
                ]}
                triggerClassName={selectTrigger}
              />
            </div>
            <div>
              <label htmlFor="pan" className={formLabelCls}>
                PAN
              </label>
              <input
                type="text"
                id="pan"
                value={pan}
                onChange={(e) => setPan(e.target.value)}
                className={`${formFieldCls} ${theme.fieldFocus}`}
                placeholder="ABCDE1234F"
              />
            </div>
          </div>
        </details>

        <details className={`${theme.sectionWrap} group`} open={inModal || undefined}>
          <summary className="cursor-pointer list-none text-sm font-medium text-slate-800">
            Address & credit terms
          </summary>
          <div className={`mt-3 ${wideGrid}`}>
            <div>
              <label htmlFor="city" className={formLabelCls}>
                City
              </label>
              <input
                type="text"
                id="city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className={`${formFieldCls} ${theme.fieldFocus}`}
              />
            </div>
            <div>
              <label htmlFor="stateName" className={formLabelCls}>
                State
              </label>
              <AppSelect
                id="stateName"
                value={stateName}
                onChange={(e) => setStateName(e.target.value)}
                placeholder="Select state"
                aria-label="State"
                sheetTitle="State"
                options={[
                  { value: '', label: 'Select state' },
                  ...indianStates.map((state) => ({ value: state, label: state })),
                ]}
                triggerClassName={selectTrigger}
              />
            </div>
            <div>
              <label htmlFor="pincode" className={formLabelCls}>
                Pincode
              </label>
              <input
                type="text"
                id="pincode"
                value={pincode}
                onChange={(e) => setPincode(e.target.value)}
                className={`${formFieldCls} ${theme.fieldFocus}`}
              />
            </div>
            <div>
              <label htmlFor="creditPeriod" className={formLabelCls}>
                Credit period (days)
              </label>
              <input
                type="number"
                id="creditPeriod"
                value={creditPeriod}
                onChange={(e) => setCreditPeriod(e.target.value)}
                className={`${formFieldCls} ${theme.fieldFocus}`}
                min="0"
              />
            </div>
            <div>
              <label htmlFor="creditLimit" className={formLabelCls}>
                Credit limit (₹)
              </label>
              <input
                type="number"
                id="creditLimit"
                value={creditLimit}
                onChange={(e) => setCreditLimit(e.target.value)}
                className={`${formFieldCls} ${theme.fieldFocus}`}
                min="0"
                step="0.01"
              />
            </div>
            <div className={inModal ? 'lg:col-span-3' : 'sm:col-span-2'}>
              <label htmlFor="address" className={formLabelCls}>
                Address
              </label>
              <textarea
                id="address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                rows={inModal ? 2 : 2}
                className={`${formFieldCls} ${theme.fieldFocus}`}
                placeholder={t('fullAddress')}
              />
            </div>
          </div>
        </details>

        <section className={`rounded-lg border p-4 ${theme.openingBalanceBox}`}>
          <details className="mb-2">
            <summary className={`text-xs font-medium cursor-pointer ${theme.openingBalanceSummary}`}>
              Opening balance guide
            </summary>
            <ul className="mt-2 text-xs text-slate-600 space-y-0.5 list-disc list-inside">
              <li>
                <strong>Positive</strong> — {t('customerOwesYou')}
              </li>
              <li>
                <strong>Negative</strong> — {t('youOweSupplier')}
              </li>
              <li>
                <strong>Zero</strong> — {t('noOpeningBalance')}
              </li>
            </ul>
          </details>
          <label htmlFor="openingBalance" className={formLabelCls}>
            Opening balance (₹)
          </label>
          <input
            type="number"
            id="openingBalance"
            value={openingBalance}
            onChange={(e) => setOpeningBalance(e.target.value)}
            className={`${formFieldCls} ${theme.fieldFocus} ${
              ob < 0 ? 'border-red-300 bg-red-50/50' : ob > 0 ? 'border-emerald-300 bg-emerald-50/50' : ''
            }`}
            placeholder="0"
            step="0.01"
          />
          {ob !== 0 && (
            <p className={`mt-1 text-xs font-medium ${ob < 0 ? 'text-red-600' : 'text-emerald-600'}`}>
              {ob < 0 ? 'Payable (you owe)' : 'Receivable (they owe you)'} — ₹
              {Math.abs(ob).toLocaleString('en-IN')}
            </p>
          )}
        </section>

        {!hideFormActions && (
          <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-slate-100">
            <button
              type="submit"
              className={`flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-semibold text-white rounded-lg bg-gradient-to-r ${theme.btn} shadow-sm ${theme.btnRing} focus:outline-none focus:ring-2 focus:ring-offset-1`}
            >
              <Save className="h-4 w-4 shrink-0" aria-hidden />
              {editingPartyId ? 'Update party' : 'Save party'}
            </button>
            <button
              type="button"
              onClick={clearPartyForm}
              className={`inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-medium bg-white border rounded-lg ${theme.btnSecondary}`}
            >
              <RotateCcw className="h-4 w-4 shrink-0" aria-hidden />
              {editingPartyId ? 'Cancel' : 'Clear'}
            </button>
          </div>
        )}
      </form>
    </>
  );

  if (embedded) {
    return (
      <div id="party-entry-form">
        {!inModal && (
          <div className="flex items-center justify-between gap-2 mb-3">
            <h3 className="text-sm font-semibold text-slate-800">
              {editingPartyId ? t('editParty') : t('addNewParty')}
            </h3>
            {editingPartyId && (
              <span className="text-[10px] font-medium uppercase tracking-wide text-emerald-800 bg-emerald-50 px-2 py-0.5 rounded-full ring-1 ring-emerald-100">
                Editing
              </span>
            )}
          </div>
        )}
        {formBody}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden p-4">
      {formBody}
    </div>
  );
}
